import React from 'react';

/**
 * ErrorBoundary - Catches and displays rendering errors
 * Prevents entire app from crashing due to component errors
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(_error, errorInfo) {
    // Log error for debugging
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    this.setState({
      error,
      errorInfo,
    });
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-neutral-50 p-4">
          <div className="max-w-md w-full bg-white rounded-2xl shadow-lg p-8">
            {/* Error Icon */}
            <div className="text-6xl mb-4 text-center">⚠️</div>

            {/* Error Title */}
            <h1 className="text-2xl font-bold text-neutral-900 mb-2 text-center">
              Something went wrong
            </h1>

            {/* Error Message */}
            <p className="text-neutral-600 mb-6 text-center">
              We encountered an unexpected error. Try refreshing the page or contact support if the
              problem persists.
            </p>

            {/* Error Details (Development Only) */}
            {import.meta.env.MODE === 'development' && this.state.error && (
              <details className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-sm">
                <summary className="font-medium text-red-900 cursor-pointer mb-2">
                  Error Details
                </summary>
                <div className="text-red-800 font-mono text-xs whitespace-pre-wrap overflow-auto max-h-40">
                  {this.state.error.toString()}
                </div>
                {this.state.errorInfo && (
                  <div className="mt-4 text-red-800 font-mono text-xs whitespace-pre-wrap overflow-auto max-h-40">
                    {this.state.errorInfo.componentStack}
                  </div>
                )}
              </details>
            )}

            {/* Action Buttons */}
            <div className="flex gap-3">
              <button
                onClick={this.handleReset}
                className="flex-1 px-4 py-2.5 bg-primary-600 hover:bg-primary-700 text-white font-medium rounded-lg transition-colors"
              >
                Try Again
              </button>
              <button
                onClick={() => (window.location.href = '/')}
                className="flex-1 px-4 py-2.5 bg-neutral-200 hover:bg-neutral-300 text-neutral-900 font-medium rounded-lg transition-colors"
              >
                Go Home
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
